import { Fragment, memo } from 'react';
import PropTypes from 'prop-types';

import { seriesColor } from '../constants/chartPalette';
import {
  ChartDataPoint,
  enterAt,
  formatRatio,
  formatValue,
  percentOf,
  WidgetPeriodChip,
} from '../shared';

/** Bar height. Tall enough to read as a band, short enough for five of them. */
const BAR_PX = 18;

/**
 * Drop-off between ordered stages.
 *
 * Every stage is drawn as a share of the **first** one and **centred**, so the
 * outline of the bars is the funnel. Left-aligned they were a bar chart sorted
 * descending, which is the same data and not the same picture — the taper is
 * what the type is named after and the only part a reader takes in without
 * reading a number.
 *
 * Stages that are not descending draw a funnel that widens, which the widget
 * does not correct: the data is wrong, and quietly sorting it would hide that.
 *
 * ## One colour, on purpose
 *
 * The stages used to rotate through four hues. Colour means "these are
 * different kinds of thing", and a funnel's stages are one thing at five
 * moments — already ordered, already labelled. Four hues invited the reader to
 * look for a distinction that is not there. Semantic colour would be worse: a
 * stage is not a pass or a failure, and green-then-red reads as one.
 *
 * ## Where the conversion goes
 *
 * `FUNNEL` publishes both readings — `pct_of_first` (how much of the entry
 * survived to here) and `pct_of_previous` (how much of the step before did).
 * They are shown, not derived: the backend applies the author's
 * `conversion_base`, and a widget recomputing both would contradict whichever
 * one they chose.
 *
 * `pct_of_previous` describes the **transition**, so it is printed in the gap
 * between two bars rather than beside either of them. Attached to the lower
 * stage it reads as a property of that stage, which is the one thing it is
 * not.
 *
 * The last stage's `pct_of_first` closes the widget as the total conversion.
 * It is the number the whole funnel is built to produce, and it used to exist
 * only inside a hover label.
 *
 * @param {object} props
 * @param {object} props.data `FUNNEL`'s `stages[]` — see propTypes.
 * @param {string} [props.accent] Overrides the bar colour.
 * @param {string} [props.className]
 */
function FunnelChartWidget({ data, accent, className = '' }) {
  const stages = data?.stages ?? [];
  const format = data?.format ?? null;
  const unit = data?.unit ?? '';
  if (!stages.length) return null;

  const entry = stages[0].value;
  const last = stages[stages.length - 1];
  const colour = seriesColor(0, accent);

  const totalPct = typeof last.pct_of_first === 'number'
    ? formatRatio(last.pct_of_first, { signed: false, decimals: 1 })
    : `${percentOf(last.value, entry)}%`;

  return (
    <div className={`flex h-full flex-col justify-center gap-1 ${className}`.trim()}>
      {stages.map((stage, index) => {
        const pct = typeof stage.pct_of_first === 'number'
          ? Math.round(stage.pct_of_first * 100)
          : percentOf(stage.value, entry);
        const previous = index > 0 ? stages[index - 1] : null;
        const step = typeof stage.pct_of_previous === 'number'
          ? formatRatio(stage.pct_of_previous, { signed: false, decimals: 1 })
          : previous ? `${percentOf(stage.value, previous.value)}%` : null;
        const enter = enterAt(index, 'left');

        return (
          <Fragment key={stage.key ?? stage.label}>
            {/* The transition, in the gap it describes. */}
            {previous && step ? (
              <div className="flex items-center gap-2.5">
                <span className="w-[62px] shrink-0" />
                <span className="min-w-0 flex-1 text-center text-[10px] text-content-faint">
                  {`↓ ${step} of previous`}
                </span>
                <span className="w-[62px] shrink-0" />
              </div>
            ) : null}

            <ChartDataPoint
              fill="block w-full"
              label={`${stage.label} · ${formatValue(stage.value, format)} of ${formatValue(entry, format)} ${unit} (${pct}% of entry)`.replace('  ', ' ')}
            >
              <div className={`flex items-center gap-2.5 ${enter.className}`} style={enter.style}>
                <span className="w-[62px] shrink-0 truncate text-[11px] text-content-muted">
                  {stage.label}
                </span>
                <span className="block min-w-0 flex-1">
                  {/* Centred: `mx-auto` on a bar of explicit width is what
                      makes the outline taper instead of step down the left. */}
                  <span
                    className="mx-auto block rounded-[3px]"
                    style={{ width: `${Math.max(pct, 1)}%`, height: BAR_PX, background: colour }}
                  />
                </span>
                <span className="w-[62px] shrink-0 text-right text-[11px] font-semibold text-content">
                  {formatValue(stage.value, format)}
                </span>
              </div>
            </ChartDataPoint>
          </Fragment>
        );
      })}

      {stages.length > 1 ? (
        <span className="mt-1 text-[10px] text-content-muted">
          {`Total conversion ${totalPct} (${stages[0].label} → ${last.label})`}
        </span>
      ) : null}

      <WidgetPeriodChip period={data?.period} className="self-start" />
    </div>
  );
}

FunnelChartWidget.propTypes = {
  /** `FUNNEL`'s `data`, as `output.schema.json` publishes it. */
  data: PropTypes.shape({
    stages: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      pct_of_first: PropTypes.number,
      pct_of_previous: PropTypes.number,
    })).isRequired,
    format: PropTypes.object,
    period: PropTypes.object,
    unit: PropTypes.string,
  }).isRequired,
  accent: PropTypes.string,
  className: PropTypes.string,
};

export default memo(FunnelChartWidget);
